import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Checkbox, Input } from '@artosera/ui';
import { formaterDate } from '../../../../lib/format.js';
import type { ControleRecette, RecetteOuverture } from '../../../../types.js';
import formStyles from '../../../../styles/form.module.css';
import pageStyles from '../../../../styles/page.module.css';
import styles from './Recette.module.css';

/**
 * La liste de contrôle (spec § 8.11) : quatre cases, avec qui les a cochées
 * et quand, et une note facultative saisie au moment de cocher. L'opérateur
 * ne voit pas les fiches : il coche ce que la galerie a constaté, ou ce qu'un
 * accès de support lui a montré.
 */
export function ListeControle({
  controles,
  modifiable,
  enCours,
  onChanger,
}: {
  controles: RecetteOuverture['controles'];
  /** L'état du dossier et le rôle de l'opérateur permettent de cocher. */
  modifiable: boolean;
  enCours: boolean;
  onChanger: (controle: ControleRecette, coche: boolean, note: string) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const cle = 'ouvertures.fiche.recette.controles';
  const [notes, setNotes] = useState<Partial<Record<ControleRecette, string>>>({});

  return (
    <section className={pageStyles.panneau} aria-labelledby="rec-controles">
      <div className={pageStyles.sectionTitre}>
        <h2 id="rec-controles">{t(`${cle}.titre`)}</h2>
      </div>
      <p className={styles.texte}>{t(`${cle}.texte`)}</p>
      {!modifiable ? <p className={formStyles.aide}>{t(`${cle}.fermee`)}</p> : null}
      <ul className={styles.cases} aria-label={t(`${cle}.liste`)}>
        {controles.map((controle) => {
          const libelle = t(`${cle}.cles.${controle.cle}`);
          return (
            <li key={controle.cle} className={styles.case} data-coche={controle.coche}>
              <div className={styles.caseTete}>
                <Checkbox
                  id={`rec-controle-${controle.cle}`}
                  label={libelle}
                  checked={controle.coche}
                  disabled={!modifiable || enCours}
                  onCheckedChange={(valeur) =>
                    onChanger(controle.cle, valeur === true, notes[controle.cle] ?? '')
                  }
                />
                {controle.coche ? (
                  <span className={styles.discret}>
                    {t(`${cle}.parLe`, {
                      par: controle.par ?? '—',
                      date: formaterDate(controle.le, '—'),
                    })}
                  </span>
                ) : (
                  <Badge tone="neutral">{t(`${cle}.aFaire`)}</Badge>
                )}
              </div>
              {controle.coche ? (
                controle.note ? (
                  <p className={styles.caseNote}>{controle.note}</p>
                ) : null
              ) : modifiable ? (
                <div className={styles.caseSaisie}>
                  <Input
                    aria-label={t(`${cle}.notePour`, { controle: libelle })}
                    placeholder={t(`${cle}.note`)}
                    maxLength={500}
                    value={notes[controle.cle] ?? ''}
                    onChange={(evenement) =>
                      setNotes((courantes) => ({
                        ...courantes,
                        [controle.cle]: evenement.target.value,
                      }))
                    }
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
